import type { ISettingsNavGroup } from "@/config/settings-nav.config";

/**
 * Case-, accent- and harakat-insensitive form for matching: "Sécurité" finds
 * "securite", and Arabic matches with or without its short-vowel marks.
 */
const normalize = (text: string) =>
  text
    .toLocaleLowerCase()
    .normalize("NFKD")
    // Combining diacritics (what NFKD splits accents into), then Arabic
    // harakat (fathatan … sukun, and the superscript alef).
    .replace(/[\u0300-\u036f\u064b-\u065f\u0670]/g, "")
    .trim();

/**
 * The sidebar's groups narrowed to what `query` matches.
 *
 * A group whose own label matches keeps all its items ("security" lists every
 * security page); otherwise an item stays when its label or one of its
 * `keywords` does. Groups left empty are dropped. An empty query returns the
 * groups untouched.
 *
 * @param translate resolves a label key — matching runs on what the user
 *   reads, in their language, not on the English key.
 */
export const filterSettingsNav = (
  groups: ISettingsNavGroup[],
  query: string,
  translate: (key: string) => string,
): ISettingsNavGroup[] => {
  const q = normalize(query);
  if (!q) return groups;
  const hits = (term: string) => normalize(term).includes(q);

  return groups
    .map((group) => ({
      ...group,
      items: hits(translate(group.label))
        ? group.items
        : group.items.filter(
            (item) =>
              hits(translate(item.label)) || (item.keywords ?? []).some(hits),
          ),
    }))
    .filter((group) => group.items.length > 0);
};
